import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
  ChangeEmailRequest,
  StaffChangePasswordRequest,
  UpdateMeRequest,
} from '@nucleus-commerce/api-client/admin';

import { session } from '@/shared/session/session';

import { accountApi } from '../api/account-api';

import { accountKeys } from './account-keys';

/** The response is /me, so the session (name in the user menu) updates in place. */
export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateMeRequest) => accountApi.update(body),
    meta: { successMessage: 'account:profile.saved', silentError: true },
    onSuccess: (me) => queryClient.setQueryData(session.queryKey, me),
  });
}

export function useChangePassword() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: StaffChangePasswordRequest) => accountApi.changePassword(body),
    meta: { successMessage: 'account:password.saved', silentError: true },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: accountKeys.sessions() }),
  });
}

export function useRequestEmailChange() {
  return useMutation({
    mutationFn: (body: ChangeEmailRequest) => accountApi.requestEmailChange(body),
    meta: { silentError: true },
  });
}

export function useConfirmEmail() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (token: string) => accountApi.confirmEmail(token),
    meta: { silentError: true },
    onSuccess: () => session.refreshSession(queryClient),
  });
}

/** Revoking this device's own session ends it; the caller sends the person to sign-in. */
export function useRevokeSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => accountApi.revokeSession(id),
    meta: { successMessage: 'account:sessions.revoked' },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: accountKeys.sessions() }),
  });
}

export function useRevokeOtherSessions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: accountApi.revokeOtherSessions,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: accountKeys.sessions() }),
  });
}
